import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, MapPin, Languages, ShieldCheck, CheckCircle2, AlertTriangle, Paperclip, Send, ChevronRight, User, FileCheck2, FileSearch, FileText, Stethoscope, Loader2, Plus, Video } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { displayEmail, isPlaceholderEmail } from '../../lib/contactEmail';
import { SPECIALTIES, packetView } from '../../lib/referrals/packet';
import { CollectabilityBadge, FundingReasons, BillTo } from './CollectabilityBadge';

// Referral Concierge — ask → match → review the packet → send. The firm
// never picks a time: the provider has one hour to accept, then schedules. Every step is the live product: the request is parsed server-side
// (Bedrock), providers come from the real directory, the packet is assembled
// from the client's real file, and "read the file" runs the client's
// documents through the same model. Nothing here is seeded.

const inputClass = 'w-full px-3 py-2 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c placeholder:text-muted-c focus:outline-none focus:border-strong transition-all';

const THINKING_LINES = [
  'Understanding the request…',
  'Searching the provider directory…',
  'Checking lien acceptance & languages…',
  'Ranking by distance…',
];

const EXAMPLES = [
  'I need a chiro near Little Havana who speaks Spanish, this week',
  'MRI center in Kendall, as soon as possible',
  'Post-concussion neurologist near Aventura',
];

const PROVIDER_TYPE_LABELS = {
  chiropractic: 'Chiropractic', mri_imaging: 'Diagnostic imaging', ortho: 'Orthopedics',
  surgery_center: 'Surgery center', hospital: 'Hospital', plastic_surgery: 'Plastic surgery',
  other: 'Medical practice',
};

// Normalize a /search result into the provider card the stages render.
function liveCard(r) {
  return {
    id: r.org_id,
    org_id: r.org_id,
    name: r.name,
    tagline: (r.specialties && r.specialties.length ? r.specialties.join(' · ') : null)
      || PROVIDER_TYPE_LABELS[r.provider_type] || 'Medical practice',
    area: [r.city, r.state].filter(Boolean).join(', '),
    distance: r.distance_miles,
    // Serves the patient's whole state by video (platform-set): listed after
    // the nearest in-person options, labeled instead of ranked by miles.
    statewide: r.telehealth_statewide ? (r.telehealth_state || r.state) : null,
    accepts_lop: r.accepts_lop,     // true | false | null (provider hasn't said)
    languages: r.languages || [],
    // Multi-location practices: the referral lands at one office. Nearest
    // to the client by default; the sender can pick another.
    locations: r.locations || [],
    nearest_location: r.nearest_location || null,
  };
}

const officeLabel = (l) => (l ? `${l.name}${l.city && !l.name.includes(l.city) ? ` · ${l.city}` : ''}${l.distance_miles != null ? ` · ${l.distance_miles.toFixed(1)} mi` : ''}` : '');

function Chip({ children, Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] text-[11px] font-semibold bg-accent-glow text-accent-light">
      {Icon && <Icon size={11} strokeWidth={2.4} />}
      {children}
    </span>
  );
}

// Editable "near <area>" — commits on Enter or blur so the search doesn't
// fire on every keystroke.
function NearbyAreaInput({ value, onChange }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => { const v = draft.trim(); if (v && v !== value) onChange(v); };
  return (
    <div className="inline-flex items-center gap-1.5 text-[13px] text-primary-c">
      <MapPin size={13} className="text-accent-light" />
      <span className="text-muted-c">near</span>
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); } }}
        className="bg-transparent border-b border-dashed border-strong focus:border-accent-light focus:outline-none font-semibold min-w-[160px]" placeholder="City or ZIP" />
    </div>
  );
}

function StepDots({ stage }) {
  const steps = ['ask', 'results', 'review'];
  const idx = Math.max(0, steps.indexOf(stage === 'sent' ? 'review' : stage === 'analyze' || stage === 'thinking' ? 'ask' : stage));
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s, i) => (
        <span key={s} className={`h-[5px] rounded-full transition-all ${i <= idx ? 'w-6 bg-accent' : 'w-3 bg-tertiary'}`} />
      ))}
    </div>
  );
}

export default function ReferralConcierge() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentOrg } = useOrg();
  const returnTo = params.get('return') || '/app/cases';
  const presetProviderId = params.get('provider') || ''; // picked on the map / profile → straight to review
  const backLabel = returnTo.startsWith('/app/contacts/') ? 'Client profile' : returnTo.startsWith('/app/cases/') ? 'Case' : 'Cases';

  const [stage, setStage] = useState('ask'); // ask | thinking | analyze | results | review | sent
  const [query, setQuery] = useState('');
  const [thinkStep, setThinkStep] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [parseSource, setParseSource] = useState(null);
  const [match, setMatch] = useState(null);
  const [provider, setProvider] = useState(null);
  const [locationId, setLocationId] = useState(''); // the office the referral is for
  const [clients, setClients] = useState(null);
  const [clientId, setClientId] = useState(params.get('client') || '');
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [referral, setReferral] = useState(null);
  const [assembling, setAssembling] = useState(false);
  // A referral is about one CASE. The firm's cases load once; the picked
  // client's cases are listed and the newest open one is chosen by default.
  const [allCases, setAllCases] = useState(null);
  const [caseId, setCaseId] = useState('');
  const [newClient, setNewClient] = useState(null); // {first_name,last_name,email,phone} while adding inline
  const [newCase, setNewCase] = useState(null);     // {case_type, accident_date} while adding inline
  const [adding, setAdding] = useState(false);
  const [sent, setSent] = useState(null); // the created referral (id, expires_at)
  const [error, setError] = useState('');
  const timersRef = useRef([]);
  // Nearest providers, shown the moment the page opens — no case, no map,
  // no typing. Anchored on the firm's own city; the area and the specialty
  // are one click to change. Picking a card goes straight to review.
  const [nearby, setNearby] = useState(null);
  const [nearbyArea, setNearbyArea] = useState('');
  const [nearbySpec, setNearbySpec] = useState('');
  const [nearbyBusy, setNearbyBusy] = useState(false);

  useEffect(() => {
    if (!currentOrg?.id) return;
    setNearbyArea((a) => a || [currentOrg.city, currentOrg.state].filter(Boolean).join(', '));
  }, [currentOrg?.id, currentOrg?.city, currentOrg?.state]);

  useEffect(() => {
    if (!currentOrg?.id || !nearbyArea) return;
    let cancelled = false;
    setNearbyBusy(true);
    api.searchReferralProviders(currentOrg.id, { near: nearbyArea, ...(nearbySpec ? { specialty: nearbySpec } : {}), limit: 8 })
      .then((res) => { if (!cancelled) setNearby((res.results || []).map(liveCard)); })
      .catch(() => { if (!cancelled) setNearby([]); })
      .finally(() => { if (!cancelled) setNearbyBusy(false); });
    return () => { cancelled = true; };
  }, [currentOrg?.id, nearbyArea, nearbySpec]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!currentOrg?.id) return;
    api.getContacts(currentOrg.id)
      .then((list) => setClients((list || []).map((c) => ({
        id: c.id,
        name: [c.first_name, c.last_name].filter(Boolean).join(' ') || displayEmail(c.email, 'Unnamed client'),
      }))))
      .catch((e) => setError(e.message || 'Could not load clients'));
    api.listCases(currentOrg.id).then((list) => setAllCases(list || [])).catch(() => setAllCases([]));
  }, [currentOrg?.id]);

  const client = useMemo(() => (clients || []).find((c) => c.id === clientId) || null, [clients, clientId]);

  // Provider chosen elsewhere (directory map or profile): load it through the
  // same search endpoint so it carries its referral settings, then open the
  // review step directly — no "describe the need" round trip.
  useEffect(() => {
    if (!currentOrg?.id || !presetProviderId) return;
    let cancelled = false;
    api.searchReferralProviders(currentOrg.id, { org_id: presetProviderId, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const r = (res.results || [])[0];
        if (!r) { setError('That provider is not accepting referrals through MediLink right now.'); return; }
        const card = liveCard(r);
        setProvider(card);
        setLocationId(card.nearest_location?.id || card.locations[0]?.id || '');
        setStage('review');
        if (clientId) assemble(clientId);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the provider'); });
    return () => { cancelled = true; };
  }, [currentOrg?.id, presetProviderId]); // eslint-disable-line
  const clientName = client?.name || '';


  const startThinking = () => {
    setStage('thinking');
    setThinkStep(0);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = THINKING_LINES.map((_, i) => setTimeout(() => setThinkStep(i + 1), 380 * (i + 1)));
  };
  const stopThinking = () => timersRef.current.forEach(clearTimeout);

  const search = async (p) => {
    const res = await api.searchReferralProviders(currentOrg.id, {
      ...(p.specialty ? { specialty: p.specialty } : {}),
      ...(p.near ? { near: p.near } : {}),
      ...(p.language ? { language: p.language } : {}),
      limit: 6,
    });
    setMatch({ results: (res.results || []).map(liveCard) });
    setStage('results');
  };

  const runSearch = async (text) => {
    const q = (text || query).trim();
    if (!q) return;
    setQuery(q);
    setMatch(null);
    setError('');
    startThinking();
    try {
      const p = await api.parseReferralQuery(currentOrg.id, q);
      setParseSource(p.source || null);
      setParsed({ specialty: p.specialty || null, near: p.near || null, language: p.language || null, urgency: p.urgency || null });
      await search(p);
    } catch (e) {
      setError(e.message || 'Search failed');
      setStage('ask');
    } finally {
      stopThinking();
    }
  };

  // Read the client's documents and let the model say what care the case
  // needs. A recommendation runs a normal directory search with the
  // client's home area and language applied.
  const analyze = async () => {
    if (!clientId) return;
    setError('');
    setAnalysis(null);
    setAnalyzing(true);
    setStage('analyze');
    try {
      setAnalysis(await api.recommendCare(currentOrg.id, clientId));
    } catch (e) {
      setError(e.message || 'Could not read the file');
      setStage('ask');
    } finally {
      setAnalyzing(false);
    }
  };

  const applyRecommendation = async (rec) => {
    const p = { specialty: rec.specialty, near: analysis?.near || null, language: analysis?.language === 'es' ? 'es' : null, urgency: null };
    setParsed(p);
    setParseSource('file');
    setError('');
    startThinking();
    try { await search(p); }
    catch (e) { setError(e.message || 'Search failed'); setStage('analyze'); }
    finally { stopThinking(); }
  };

  const pickProvider = (p) => {
    setProvider(p);
    setLocationId(p.nearest_location?.id || p.locations[0]?.id || '');
    setStage('review');
    if (clientId) assemble(clientId);
  };

  const casesFor = (id) => (allCases || []).filter((c) => c.client_id === id && !['settled', 'closed'].includes(c.status));

  const assemble = async (id, chosenCaseId) => {
    setClientId(id);
    setReferral(null);
    setNewClient(null); setNewCase(null);
    if (!id) { setCaseId(''); return; }
    const cid = chosenCaseId !== undefined ? chosenCaseId : (casesFor(id)[0]?.id || '');
    setCaseId(cid);
    setAssembling(true);
    setError('');
    try {
      const a = await api.assembleReferralPacket(currentOrg.id, id, { provider_org_id: provider?.org_id, case_id: cid || undefined });
      const spec = parsed?.specialty ? SPECIALTIES[parsed.specialty] : null;
      setReferral({
        client: a.client_snapshot || {},
        insurance: a.insurance || {},
        liens: {},
        packet: a.packet || {},
        billing: a.billing || null,
        case_id: a.case_id || null,
        service: spec ? spec.service : `Referral — ${provider?.tagline || 'evaluation'}`,
        document_count: a.document_count || 0,
      });
    } catch (e) {
      setError(e.message || 'Could not assemble the packet');
    } finally {
      setAssembling(false);
    }
  };

  const addClient = async () => {
    if (!newClient?.first_name || !newClient?.last_name) { setError('First and last name are required'); return; }
    setAdding(true); setError('');
    try {
      const created = await api.createContact(currentOrg.id, newClient);
      const id = created.id || created.user?.id;
      setClients((prev) => [...(prev || []), { id, name: `${newClient.first_name} ${newClient.last_name}` }]);
      setNewClient(null);
      await assemble(id, '');
    } catch (e) { setError(e.message || 'Could not add the client'); }
    finally { setAdding(false); }
  };

  const addCase = async () => {
    if (!newCase?.case_type) { setError('Pick a case type'); return; }
    setAdding(true); setError('');
    try {
      const cs = await api.createCase(currentOrg.id, { client_id: clientId, case_type: newCase.case_type, accident_date: newCase.accident_date || null });
      setAllCases((prev) => [cs, ...(prev || [])]);
      setNewCase(null);
      await assemble(clientId, cs.id);
    } catch (e) { setError(e.message || 'Could not create the case'); }
    finally { setAdding(false); }
  };

  const send = async () => {
    setError('');
    try {
      const created = await api.createReferral(currentOrg.id, {
        provider_org_id: provider.org_id,
        client_id: clientId,
        specialty: parsed?.specialty || null,
        service: referral.service,
        case_id: caseId || referral.case_id || undefined,
        location_id: locationId || undefined,
        via: 'concierge',
      });
      setSent(created);
      setStage('sent');
    } catch (e) {
      setError(e.message || 'Send failed');
    }
  };

  const resultChips = () => {
    if (!parsed) return [];
    const c = [parsed.specialty ? SPECIALTIES[parsed.specialty].label : 'Any specialty'];
    if (parsed.near) c.push(`Near ${parsed.near}`);
    if (parsed.language === 'es') c.push('Spanish-speaking');
    if (parsed.urgency === 'asap') c.push('As soon as possible');
    else if (parsed.urgency === 'tomorrow') c.push('By tomorrow');
    else if (parsed.urgency === 'week') c.push('This week');
    c.push(parseSource === 'bedrock' ? 'Understood by Claude' : parseSource === 'file' ? 'From the case file' : 'Keyword match');
    return c;
  };

  const packet = useMemo(() => (referral ? packetView(referral) : null), [referral]);

  const ClientPicker = ({ compact }) => (
    clients == null ? <div className="text-[12px] text-muted-c">Loading your clients…</div> : (
      <select value={clientId} onChange={(e) => (stage === 'review' ? assemble(e.target.value) : setClientId(e.target.value))} className={`${inputClass} ${compact ? 'max-w-[320px]' : ''}`}>
        <option value="">— pick a client —</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    )
  );

  return (
    <main className="flex-1 bg-primary">
      <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
        <button onClick={() => navigate(returnTo)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-c hover:text-accent-light transition-all mb-4">
          <ArrowLeft size={13} strokeWidth={2.4} /> {backLabel}
        </button>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} strokeWidth={2.2} className="text-accent-light" />
              <span className="text-[11px] uppercase tracking-wide text-accent-light font-semibold">Referral Concierge</span>
            </div>
            <h1 className="text-[24px] font-bold text-primary-c">
              {stage === 'sent' ? 'Referral sent' : stage === 'analyze' ? `Reading ${clientName || 'the'} file` : stage === 'review' && provider ? `Refer a case to ${provider.name}` : 'Find your client a provider'}
            </h1>
          </div>
          <StepDots stage={stage} />
        </div>

        {error && (
          <div className="rounded-[10px] bg-danger-soft text-danger text-[12px] font-semibold px-3 py-2 mb-4">{error}</div>
        )}

        {/* ── Ask ── */}
        {(stage === 'ask' || stage === 'thinking') && (
          <div>
            <p className="text-[13px] text-muted-c max-w-[620px] mb-5">
              Describe what your client needs — specialty, area, language, how soon. The concierge searches
              your provider directory and assembles the referral packet from the case file. Nothing gets re-typed.
            </p>
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {client ? (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-accent-glow text-accent-light text-[12px] font-semibold">
                  <User size={12} strokeWidth={2.4} /> Referring {clientName}
                  <button onClick={() => { setClientId(''); setAnalysis(null); }} className="text-muted-c hover:text-primary-c ml-1">Change</button>
                </span>
              ) : (
                <div className="flex items-center gap-2"><span className="text-[12px] text-muted-c">Client:</span><ClientPicker compact /></div>
              )}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); runSearch(); }}
              className="rounded-[14px] border border-theme bg-elevated p-2 flex items-center gap-2 shadow-glow"
            >
              <Sparkles size={16} strokeWidth={2} className="text-accent-light ml-2 flex-shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={stage === 'thinking'}
                placeholder="I need a chiro near Little Havana who speaks Spanish, this week…"
                className="flex-1 bg-transparent text-[14px] text-primary-c placeholder:text-muted-c focus:outline-none px-1 py-2.5 min-w-0"
              />
              <button type="submit" disabled={stage === 'thinking' || !query.trim()} className="px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent hover:opacity-95 transition-all disabled:opacity-50 whitespace-nowrap">
                Find providers
              </button>
            </form>

            {stage === 'thinking' ? (
              <div className="mt-6 flex flex-col gap-2.5">
                {THINKING_LINES.map((line, i) => (
                  <div key={line} className={`flex items-center gap-2.5 text-[12.5px] transition-all ${i < thinkStep ? 'text-secondary-c' : i === thinkStep ? 'text-primary-c font-medium' : 'text-muted-c opacity-40'}`}>
                    {i < thinkStep
                      ? <CheckCircle2 size={13} strokeWidth={2.4} className="text-success flex-shrink-0" />
                      : <span className={`w-[13px] h-[13px] rounded-full border-2 flex-shrink-0 ${i === thinkStep ? 'border-accent animate-pulse' : 'border-theme'}`} />}
                    {line}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {EXAMPLES.map((s) => (
                    <button key={s} onClick={() => runSearch(s)} className="px-3 py-1.5 rounded-[10px] border border-theme bg-tertiary text-[12px] text-muted-c hover:text-accent-light hover:border-strong transition-all">
                      {s}
                    </button>
                  ))}
                </div>

                {/* Nearest providers — there before anyone types */}
                <div className="mt-7">
                  <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-1">Nearest providers</div>
                      <NearbyAreaInput value={nearbyArea} onChange={setNearbyArea} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => setNearbySpec('')} className={`px-2.5 py-1 rounded-[8px] text-[11.5px] font-semibold transition-all ${!nearbySpec ? 'bg-accent text-white shadow-glow' : 'bg-tertiary text-muted-c hover:text-primary-c'}`}>Any</button>
                      {Object.entries(SPECIALTIES).map(([k, v]) => (
                        <button key={k} onClick={() => setNearbySpec(k)} className={`px-2.5 py-1 rounded-[8px] text-[11.5px] font-semibold transition-all ${nearbySpec === k ? 'bg-accent text-white shadow-glow' : 'bg-tertiary text-muted-c hover:text-primary-c'}`}>{v.label}</button>
                      ))}
                    </div>
                  </div>
                  {nearby === null || nearbyBusy ? (
                    <div className="text-[12.5px] text-muted-c flex items-center gap-2 py-4"><Loader2 size={13} className="animate-spin" /> Finding providers near {nearbyArea || 'you'}…</div>
                  ) : nearby.length === 0 ? (
                    <div className="rounded-[14px] border border-theme bg-elevated p-4 text-[12.5px] text-muted-c">No providers near {nearbyArea} yet. Try a nearby city or describe the need above.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {nearby.map((p) => (
                        <button key={p.id} onClick={() => pickProvider(p)} className="text-left rounded-[14px] border border-theme bg-elevated p-3.5 hover:border-strong hover:bg-hover-surface transition-all flex items-center gap-3 group">
                          <div className="w-[38px] h-[38px] rounded-[10px] gradient-accent flex items-center justify-center flex-shrink-0 text-white font-bold text-[14px]">{p.name.charAt(0)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[13px] font-semibold text-primary-c truncate">{p.name}</span>
                              {p.accepts_lop === true && <span className="px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold bg-success-soft text-success">LOP</span>}
                            </div>
                            <div className="text-[11.5px] text-muted-c truncate">{p.tagline}</div>
                            {p.statewide
                              ? <div className="text-[11px] font-semibold text-accent-light inline-flex items-center gap-1"><Video size={10} /> Video visits · anywhere in {p.statewide}</div>
                              : <div className="text-[11px] text-muted-c inline-flex items-center gap-1"><MapPin size={10} /> {p.nearest_location && p.locations.length > 1 ? `${p.nearest_location.name} · ` : ''}{p.area}{p.distance != null ? ` · ${p.distance.toFixed(1)} mi` : ''}</div>}
                          </div>
                          <ChevronRight size={14} className="text-muted-c group-hover:text-accent-light flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-c mt-2">Pick a provider, then choose the client on the next screen — or add a new client right there.</p>
                </div>

                <div className="mt-7 rounded-[14px] border border-theme bg-elevated p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FileSearch size={14} strokeWidth={2.2} className="text-accent-light" />
                    <span className="text-[12px] font-semibold text-primary-c">Not sure what care the case needs?</span>
                  </div>
                  <p className="text-[12px] text-muted-c mb-3">
                    Read the client&apos;s documents on file — ER notes, crash report, imaging — and get the
                    recommended provider type with the findings that support it. Runs on Claude inside your AWS BAA.
                  </p>
                  {client ? (
                    <button onClick={analyze} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all">
                      <FileSearch size={14} strokeWidth={2.4} /> Read {clientName}&apos;s file
                    </button>
                  ) : (
                    <div className="text-[12px] text-muted-c">Pick a client above to read their file.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Case analysis ── */}
        {stage === 'analyze' && (
          <div>
            {analyzing && (
              <div className="rounded-[14px] border border-theme bg-elevated p-5 mb-4 text-[13px] text-muted-c flex items-center gap-2.5">
                <Loader2 size={15} className="animate-spin text-accent-light" /> Reading {clientName}&apos;s documents… this takes 10–20 seconds.
              </div>
            )}
            {analysis && (
              <div>
                <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4">
                  <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-3">
                    {analysis.read?.length || 0} document{analysis.read?.length === 1 ? '' : 's'} read
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {(analysis.read || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-2.5 text-[12.5px] text-secondary-c">
                        <CheckCircle2 size={13} strokeWidth={2.4} className="text-success flex-shrink-0" />
                        <span className="font-mono text-[11.5px] truncate">{d.name}</span>
                      </div>
                    ))}
                    {(analysis.skipped || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-2.5 text-[12.5px] text-muted-c">
                        <AlertTriangle size={13} className="text-warning flex-shrink-0" />
                        <span className="font-mono text-[11.5px] truncate">{d.name}</span><span className="text-[11px]">{d.reason}</span>
                      </div>
                    ))}
                  </div>
                  {analysis.summary && <p className="mt-3 pt-3 border-t border-theme text-[12.5px] text-primary-c">{analysis.summary}</p>}
                </div>

                {analysis.findings?.length > 0 && (
                  <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4">
                    <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-3">What the file shows</div>
                    <div className="flex flex-col gap-2.5">
                      {analysis.findings.map((f, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <FileSearch size={13} strokeWidth={2.2} className="text-accent-light mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-[12.5px] text-primary-c">{f.text}</div>
                            <div className="text-[10.5px] text-muted-c font-mono truncate">{f.source_document}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-[13px] font-semibold text-primary-c mb-2">Recommended care</div>
                {analysis.recommendations?.length === 0 && (
                  <div className="rounded-[14px] border border-theme bg-elevated p-4 text-[12.5px] text-muted-c">The documents don&apos;t support a specific provider type yet. Describe the need above instead.</div>
                )}
                <div className="flex flex-col gap-3">
                  {(analysis.recommendations || []).map((rec) => (
                    <div key={rec.specialty} className="rounded-[14px] border border-theme bg-elevated p-4 flex items-start gap-3 flex-wrap md:flex-nowrap">
                      <div className="w-[38px] h-[38px] rounded-[10px] gradient-accent flex items-center justify-center flex-shrink-0 text-white">
                        <Stethoscope size={16} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[14px] font-semibold text-primary-c">{SPECIALTIES[rec.specialty]?.label || rec.specialty}</span>
                          <span className={`px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold ${rec.confidence === 'high' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
                            {rec.confidence === 'high' ? 'Recommended first' : 'Consider next'}
                          </span>
                          {analysis.language === 'es' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold bg-accent-glow text-accent-light">
                              <Languages size={10} strokeWidth={2.4} /> Spanish preferred
                            </span>
                          )}
                        </div>
                        <p className="text-[12.5px] text-muted-c leading-relaxed">{rec.rationale}</p>
                      </div>
                      <button onClick={() => applyRecommendation(rec)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all flex-shrink-0">
                        Find providers{analysis.near ? ` near ${analysis.near}` : ''}
                        <ChevronRight size={13} strokeWidth={2.4} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex items-center gap-4">
                  <button onClick={() => setStage('ask')} className="text-[12px] font-semibold text-muted-c hover:text-accent-light transition-all">Back</button>
                  <div className="text-[11px] text-muted-c">Findings are drawn from the documents on file and cite their source — review before relying on them.</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Results ── */}
        {stage === 'results' && match && (
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {resultChips().map((c) => <Chip key={c}>{c}</Chip>)}
            </div>
            <p className="text-[12px] text-muted-c mb-5">
              {match.results.length} provider{match.results.length === 1 ? '' : 's'} from your directory{match.results.some((r) => r.distance != null) ? ', closest first' : ''}.
              {match.results.length === 0 && ' Try a broader area or a different specialty.'}
            </p>

            <div className="flex flex-col gap-3">
              {match.results.map((p) => (
                <button key={p.id} onClick={() => pickProvider(p)} className="w-full text-left rounded-[14px] border border-theme bg-elevated p-4 hover:border-strong hover:bg-hover-surface transition-all flex items-center gap-4 group">
                  <div className="w-[42px] h-[42px] rounded-[12px] gradient-accent flex items-center justify-center flex-shrink-0 text-white font-bold text-[15px]">{p.name.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[14px] font-semibold text-primary-c">{p.name}</span>
                      {p.accepts_lop === true && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold bg-success-soft text-success"><ShieldCheck size={10} strokeWidth={2.6} /> Accepts LOP</span>
                      )}
                      {p.accepts_lop === false && <span className="px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold bg-warning-soft text-warning">No LOP</span>}
                      {p.accepts_lop == null && <span className="px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold bg-tertiary text-muted-c">LOP unconfirmed</span>}
                    </div>
                    <div className="text-[12px] text-muted-c mb-1">{p.tagline}</div>
                    <div className="flex items-center gap-3 flex-wrap text-[11.5px] text-muted-c">
                      {p.statewide ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-accent-light"><Video size={11} strokeWidth={2.2} /> Video visits · anywhere in {p.statewide}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} strokeWidth={2.2} /> {p.nearest_location && p.locations.length > 1 ? `${p.nearest_location.name} · ` : ''}{p.area}{p.distance != null ? ` · ${p.distance.toFixed(1)} mi` : ''}
                          {p.locations.length > 1 ? ` · ${p.locations.length} offices` : ''}
                        </span>
                      )}
                      {p.languages.length > 0 && (
                        <span className="inline-flex items-center gap-1"><Languages size={11} strokeWidth={2.2} /> {p.languages.map((l) => (l === 'es' ? 'Spanish' : 'English')).join(' · ')}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={15} strokeWidth={2.2} className="text-muted-c group-hover:text-accent-light flex-shrink-0" />
                </button>
              ))}
            </div>

            <button onClick={() => setStage(analysis ? 'analyze' : 'ask')} className="mt-5 text-[12px] font-semibold text-muted-c hover:text-accent-light transition-all">Change the request</button>
          </div>
        )}

        {/* ── Review ── */}
        {stage === 'review' && (
          <div>
            <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[13px] font-semibold text-primary-c">{provider.name}</div>
                <div className="text-[11.5px] text-muted-c">
                  {referral ? referral.service : provider.tagline}{provider.area ? ` · ${provider.area}` : ''}
                </div>
              </div>
              {provider.locations.length > 1 ? (
                <label className="flex items-center gap-2 text-[12px] text-muted-c">
                  <MapPin size={12} className="text-accent-light shrink-0" />
                  <span className="font-semibold">Office</span>
                  <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={`${inputClass} w-auto max-w-[280px]`} title="The office this referral goes to — its inbox, calendar and provider hours">
                    {provider.locations.map((l) => <option key={l.id} value={l.id}>{officeLabel(l)}</option>)}
                  </select>
                </label>
              ) : provider.locations[0] ? (
                <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-c"><MapPin size={11} /> {provider.locations[0].name}</span>
              ) : null}
              <button onClick={() => setStage(match ? 'results' : 'ask')} className="text-[12px] font-semibold text-muted-c hover:text-accent-light transition-all">Change provider</button>
            </div>

            <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold">Client</div>
                {!newClient && (
                  <button onClick={() => { setNewClient({ first_name: '', last_name: '', email: '', phone: '', sms_opt_in: false }); setError(''); }} className="inline-flex items-center gap-1 text-[12px] font-semibold text-accent-light hover:text-accent"><Plus size={12} /> New client</button>
                )}
              </div>
              {newClient ? (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  {[['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email (optional)'], ['phone', 'Phone']].map(([k, ph]) => (
                    <input key={k} placeholder={ph} value={newClient[k] || ''} onChange={(e) => setNewClient({ ...newClient, [k]: e.target.value })} className={inputClass} />
                  ))}
                  <label className="sm:col-span-4 flex items-start gap-2 cursor-pointer"><input type="checkbox" checked={!!newClient.sms_opt_in} onChange={(e) => setNewClient({ ...newClient, sms_opt_in: e.target.checked })} className="mt-0.5 accent-[#0DA7CA]" /><span className="text-[11.5px] text-muted-c leading-snug">Patient agreed to receive appointment &amp; referral texts at this number. No medical details are texted; STOP opts out.</span></label>
                  <div className="sm:col-span-4 flex items-center gap-3">
                    <button onClick={addClient} disabled={adding} className="px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">{adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add client</button>
                    <button onClick={() => setNewClient(null)} className="text-[12px] text-muted-c">Cancel</button>
                  </div>
                </div>
              ) : <ClientPicker />}

              {clientId && !newClient && (
                <div className="mt-4 pt-4 border-t border-theme">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold">Case being referred</div>
                    {!newCase && (
                      <button onClick={() => { setNewCase({ case_type: 'auto_mva', accident_date: '' }); setError(''); }} className="inline-flex items-center gap-1 text-[12px] font-semibold text-accent-light hover:text-accent"><Plus size={12} /> New case</button>
                    )}
                  </div>
                  {allCases == null ? (
                    <div className="text-[12px] text-muted-c">Loading cases…</div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {casesFor(clientId).map((c) => (
                        <label key={c.id} className={`flex items-center gap-3 px-3 py-2 rounded-[10px] border cursor-pointer ${caseId === c.id ? 'border-accent-light bg-accent-glow' : 'border-theme bg-tertiary hover:border-strong'}`}>
                          <input type="radio" name="case" checked={caseId === c.id} onChange={() => assemble(clientId, c.id)} />
                          <span className="font-mono text-[11.5px] text-muted-c">{c.case_number}</span>
                          <span className="text-[12.5px] text-primary-c">{{ auto_mva: 'Auto (MVA)', premises: 'Premises', other: 'Other PI' }[c.case_type] || c.case_type}{c.accident_date ? ` · injured ${c.accident_date}` : ''}</span>
                          <span className="ml-auto text-[11px] text-secondary-c whitespace-nowrap">{c.funding?.current?.chip || ''}</span>
                        </label>
                      ))}
                      {casesFor(clientId).length === 0 && !newCase && (
                        <div className="text-[12px] text-warning">No open case on file for this client — add one so the clinic knows what to bill.</div>
                      )}
                    </div>
                  )}
                  {newCase && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select value={newCase.case_type} onChange={(e) => setNewCase({ ...newCase, case_type: e.target.value })} className={inputClass}>
                        <option value="auto_mva">Auto (MVA)</option><option value="premises">Premises</option><option value="other">Other PI</option>
                      </select>
                      <input type="date" value={newCase.accident_date} onChange={(e) => setNewCase({ ...newCase, accident_date: e.target.value })} className={inputClass} title="Date of injury" />
                      <div className="flex items-center gap-3">
                        <button onClick={addCase} disabled={adding} className="px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">{adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add case</button>
                        <button onClick={() => setNewCase(null)} className="text-[12px] text-muted-c">Cancel</button>
                      </div>
                      <div className="sm:col-span-3 text-[11px] text-muted-c">
                        Quick case: type and date of injury. For the full setup — upload the crash report, ER records and PIP log and let the AI fill the case — use <Link to={`/app/cases/new?client=${clientId}`} className="text-accent-light font-semibold">New case</Link>.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {assembling && (
              <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4 text-[12px] text-muted-c flex items-center gap-2">
                <Loader2 size={13} className="animate-spin text-accent-light" /> Assembling the packet from the case file…
              </div>
            )}

            {referral && packet && (
              <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4">
                <BillTo referral={referral} />
                <FundingReasons referral={referral} className="mt-3" />
                {packet.billing?.mode === 'none' && (
                  <p className="text-[12px] text-warning mt-2">This client has no coverage recorded on their case. Add PIP, health insurance or an LOP on the case page so the clinic knows what to bill.</p>
                )}

                <div className="flex items-center justify-between gap-3 mt-5 mb-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold">What {provider.name} needs · {packet.done}/{packet.total} from the case file</div>
                </div>
                <p className="text-[12px] text-muted-c mb-3">
                  Only what this payer requires. Everything below transmits with the referral — assembled from {clientName ? `${clientName}'s` : 'the'} file, nothing re-typed.
                  {referral.document_count === 0 && ' No documents are on file for this client yet.'}
                </p>
                <div className="flex flex-col divide-y divide-theme">
                  {packet.items.map((item) => {
                    const optional = item.required === false;
                    return (
                      <div key={item.key} className={`flex items-center gap-3 py-2.5 first:pt-0 last:pb-0 ${optional && !item.done ? 'opacity-60' : ''}`}>
                        {item.done ? <CheckCircle2 size={15} strokeWidth={2.4} className="text-success flex-shrink-0" /> : <AlertTriangle size={15} strokeWidth={2.4} className={`${optional ? 'text-muted-c' : 'text-warning'} flex-shrink-0`} />}
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-primary-c">{item.label}{optional && <span className="ml-2 text-[10.5px] font-semibold text-muted-c uppercase">optional</span>}</div>
                          {!item.done && <div className={`text-[11px] ${optional ? 'text-muted-c' : 'text-warning'}`}>{item.kind === 'doc' ? 'Not found in the file' : 'Not recorded on the case'}{item.hint ? ` · ${item.hint}` : ''}</div>}
                        </div>
                        {item.done && item.file && (
                          <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-muted-c font-mono truncate max-w-[220px]"><Paperclip size={11} strokeWidth={2.2} /> {item.file}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-muted-c max-w-[440px]">
                {referral
                  ? `${provider.name} gets the payer details and the ${packet.done}/${packet.total} items above, then has one hour to accept and schedule the first visit. Missing items can be added to the case any time.`
                  : 'Pick a client and a case — the packet assembles from that case file.'}
              </div>
              <button onClick={send} disabled={!referral || assembling} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all disabled:opacity-50">
                <Send size={14} strokeWidth={2.4} /> Send referral
              </button>
            </div>
          </div>
        )}

        {/* ── Sent ── */}
        {stage === 'sent' && (
          <div className="rounded-[14px] border border-theme bg-elevated p-8 text-center">
            <div className="w-[52px] h-[52px] rounded-full bg-success-soft text-success flex items-center justify-center mx-auto mb-4"><FileCheck2 size={22} strokeWidth={2.2} /></div>
            <div className="text-[16px] font-bold text-primary-c mb-1">Referral sent to {provider?.name}</div>
            <p className="text-[13px] text-muted-c max-w-[480px] mx-auto mb-2">
              {clientName} · {referral?.service}. The clinic received the assembled case file and has <strong className="text-primary-c">until {sent?.expires_at ? new Date(sent.expires_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'the top of the hour'}</strong> to accept.
            </p>
            <p className="text-[12px] text-muted-c max-w-[480px] mx-auto mb-4">
              Once they accept, they schedule the first visit and you get the date by email. If the hour passes with no answer, the referral expires and comes back to you to send elsewhere.
            </p>
            {sent?.id && <div className="text-[11px] text-muted-c font-mono mb-6">referral {sent.id}</div>}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button onClick={() => navigate('/app/referrals')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all">
                <FileText size={14} strokeWidth={2.4} /> Track it in Referrals
              </button>
              <button onClick={() => navigate(returnTo)} className="px-4 py-2.5 rounded-[10px] border border-theme bg-tertiary text-[13px] font-semibold text-muted-c hover:text-accent-light transition-all">
                Back to {backLabel.toLowerCase()}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
